import { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControlLabel, Switch, Tooltip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import { InfoTip } from '../LabelWithInfo';

const emptyForm = { code: '', name: '', maxDays: '', renewable: false, statusActive: true };

/**
 * Catálogo de causales de contratación (PLAN_ERP.md §11): código, nombre,
 * límite legal de días y si admite renovación. Editable por pantalla — el
 * seed con las causales EST reales lo entrega David (pregunta abierta 2).
 */
const ErpHiringCausesTab = () => {
  const { t } = useTranslation();
  const { organization, authToken, showAlert } = useStore.getState();

  const [causes, setCauses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/erp/hiring-causes', {
        params: { organizationId: organization },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.data.response?.status_response === 'OK') {
        setCauses(response.data.response.data || []);
      } else {
        setError(t('erp.hiringCauses.loadError'));
      }
    } catch (err) {
      console.error(err);
      setError(t('erp.hiringCauses.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organization) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (cause) => {
    setEditing(cause.uid);
    setForm({
      code: cause.code || '',
      name: cause.name || '',
      maxDays: cause.maxDays ?? '',
      renewable: Boolean(cause.renewable),
      statusActive: cause.status === 'active',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      showAlert(t('erp.common.requiredFields'), 'warning');
      return;
    }

    const payload = {
      organizationId: organization,
      code: form.code.trim(),
      name: form.name.trim(),
      maxDays: form.maxDays === '' ? null : Number(form.maxDays),
      renewable: form.renewable,
      status: form.statusActive ? 'active' : 'inactive',
    };

    try {
      setSaving(true);
      const response = editing
        ? await api.put(`/erp/hiring-causes/${editing}`, payload, {
            headers: { Authorization: `Bearer ${authToken}` },
          })
        : await api.post('/erp/hiring-causes', payload, {
            headers: { Authorization: `Bearer ${authToken}` },
          });

      if (response.data.response?.status_response === 'OK') {
        showAlert(t('erp.hiringCauses.saveSuccess'), 'success');
        setDialogOpen(false);
        load();
      } else {
        showAlert(t('erp.hiringCauses.saveError'), 'error');
      }
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.errorMessage || '';
      showAlert(
        message.includes('already exists') ? t('erp.hiringCauses.duplicateCode') : t('erp.hiringCauses.saveError'),
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" fontWeight={600}>{t('erp.hiringCauses.title')}</Typography>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreate}
          sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}>
          {t('erp.hiringCauses.add')}
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('erp.hiringCauses.subtitle')}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
      ) : causes.length === 0 ? (
        <Alert severity="info">{t('erp.hiringCauses.empty')}</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('erp.hiringCauses.table.code')}</TableCell>
                <TableCell>{t('erp.hiringCauses.table.name')}</TableCell>
                <TableCell>{t('erp.hiringCauses.table.maxDays')}</TableCell>
                <TableCell>{t('erp.hiringCauses.table.renewable')}</TableCell>
                <TableCell>{t('erp.common.status')}</TableCell>
                <TableCell align="right">{t('erp.common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {causes.map((cause) => (
                <TableRow key={cause.uid} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{cause.code}</TableCell>
                  <TableCell>{cause.name}</TableCell>
                  <TableCell>{cause.maxDays ?? '—'}</TableCell>
                  <TableCell>{cause.renewable ? t('yes') : t('no')}</TableCell>
                  <TableCell>
                    <Chip size="small"
                      label={cause.status === 'active' ? t('erp.common.active') : t('erp.common.inactive')}
                      color={cause.status === 'active' ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('erp.hiringCauses.edit')}>
                      <IconButton size="small" onClick={() => openEdit(cause)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? t('erp.hiringCauses.edit') : t('erp.hiringCauses.add')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          <TextField fullWidth size="small" required label={t('erp.hiringCauses.form.code')}
            helperText={t('erp.tooltips.causes.code')}
            value={form.code} onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))} />
          <TextField fullWidth size="small" required label={t('erp.hiringCauses.form.name')}
            value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          <TextField fullWidth size="small" type="number" label={t('erp.hiringCauses.form.maxDays')}
            helperText={t('erp.hiringCauses.form.maxDaysHelper')}
            value={form.maxDays} onChange={(e) => setForm((p) => ({ ...p, maxDays: e.target.value }))} />
          <FormControlLabel
            control={<Switch checked={form.renewable}
              onChange={(e) => setForm((p) => ({ ...p, renewable: e.target.checked }))} />}
            label={(<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>{t('erp.hiringCauses.form.renewable')}<InfoTip title={t('erp.tooltips.causes.renewable')} /></Box>)} />
          {editing && (
            <FormControlLabel
              control={<Switch checked={form.statusActive}
                onChange={(e) => setForm((p) => ({ ...p, statusActive: e.target.checked }))} />}
              label={form.statusActive ? t('erp.common.active') : t('erp.common.inactive')} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>{t('erp.common.cancel')}</Button>
          <Button variant="contained" disabled={saving} onClick={handleSave}
            sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}>
            {saving ? t('erp.common.saving') : t('erp.common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ErpHiringCausesTab;
